import { useState } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { AlertOutlineIcon, CheckIcon, CopyIcon as ArcadeCopyIcon, FailedIcon } from '@arcade/web/icons/react'
import { Button, Flex, InteractiveCell, PaddingInset, spacing, Stack, Text } from '@cash-design-system/react'
// eslint-disable-next-line no-restricted-imports
import { TypographyIdentifierLetterSpacing, TypographyIdentifierTextTransform } from '@cash-design-system/tokens'
import {
  FormBlockerElementCopyableElementGroup,
  FormBlockerElementCopyableElementGroupCopyableElement,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { WebInteractClick } from '@squareup/customer-data-events-web'

const CDF_COMPONENT_NAME = 'CopyableElementGroup'

const CopyIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.9 1.5H6.047c-.23 0-.554 0-.848.024a4.012 4.012 0 0 0-1.515.412 4 4 0 0 0-1.748 1.748A4.012 4.012 0 0 0 1.524 5.2c-.024.294-.024.619-.024.848v6.906c0 .23 0 .554.024.848.028.352.106.915.412 1.515a4 4 0 0 0 1.748 1.748c.6.306 1.163.384 1.515.413.274.022.576.023.801.023v-3c-.494 0-.753-.007-.954-.109a1 1 0 0 1-.437-.437C4.5 13.74 4.5 13.46 4.5 12.9V6.1c0-.56 0-.84.109-1.054a1 1 0 0 1 .437-.437C5.26 4.5 5.54 4.5 6.1 4.5h5.8c.56 0 .84 0 1.054.109a1 1 0 0 1 .412.391h3.09a3.92 3.92 0 0 0-.392-1.316 4 4 0 0 0-1.748-1.748 4.012 4.012 0 0 0-1.515-.412 10.692 10.692 0 0 0-.848-.024H11.9Z"
      fill={color ?? 'currentColor'}
    />
    <rect
      x="9"
      y="8"
      width="12"
      height="13"
      rx="2.5"
      stroke={color ?? 'currentColor'}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`

const Label = styled.div`
  ${({ theme }) => theme.typography.identifier}
  color: ${({ theme }) => theme.colors.tertiaryLabel};
  letter-spacing: ${TypographyIdentifierLetterSpacing}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  text-transform: ${TypographyIdentifierTextTransform};
`

const StyledCheckIcon = styled(CheckIcon)`
  margin-right: ${({ theme }) => theme.spacing.xs};
`

const StyledAlertOutlineIcon = styled(AlertOutlineIcon)`
  margin-right: ${({ theme }) => theme.spacing.xs};
`

const CopyStatus = styled.div`
  ${({ theme }) => theme.typography.button};
  display: flex;
  align-items: center;
`

const CopySuccess = styled(CopyStatus)`
  color: ${({ theme }) => theme.colors.tertiaryLabel};
`

const CopyFailed = styled(CopyStatus)`
  color: ${({ theme }) => theme.colors.warning};
`

const DefaultValueContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text typography="labelMedium" as="div">
      {children}
    </Text>
  )
}

export const CopyableElement = (
  props: FormBlockerElementCopyableElementGroupCopyableElement & {
    ValueContainer?: React.FunctionComponent<{ children: React.ReactNode }>
  }
) => {
  const theme = useTheme()
  const logCdfEvent = useLogCashWebCdfEvent()
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const [statusMessage, setStatusMessage] = useState<'success' | 'failed' | undefined>()
  const [accessoryIcon, setAccessoryIcon] = useState(
    <Text color="textStandard" typography="largeLabel">
      <ArcadeCopyIcon size={24} />
    </Text>
  )

  if (!props.value) {
    return null
  }

  // So that we don't need to do any fancy typechecking below, we redefined props.value here since at this point we know
  // it is definitely not undefined.
  const valueAsDefiniteString = props.value

  const handleCopyArcade = async () => {
    logCdfEvent(WebInteractClick({ componentName: CDF_COMPONENT_NAME, componentType: `Copy[label:${props.label}]` }))

    try {
      await navigator.clipboard.writeText(valueAsDefiniteString)
      setAccessoryIcon(
        <Text color="textSuccess" typography="largeLabel">
          <CheckIcon size={24} />
        </Text>
      )
    } catch {
      setAccessoryIcon(
        <Text color="textWarning" typography="largeLabel">
          <FailedIcon size={24} />
        </Text>
      )
    }

    // Only show the status for 2 seconds (to match mobile)
    const twoSeconds = 2000
    setTimeout(() => {
      setAccessoryIcon(
        <Text color="textStandard" typography="largeLabel">
          <ArcadeCopyIcon size={24} />
        </Text>
      )
    }, twoSeconds)
  }
  const handleCopyButton = async () => {
    logCdfEvent(WebInteractClick({ componentName: CDF_COMPONENT_NAME, componentType: `Copy[label:${props.label}]` }))

    try {
      await navigator.clipboard.writeText(valueAsDefiniteString)
      setStatusMessage('success')
    } catch {
      setStatusMessage('failed')
    }

    // Only show the status for 3 seconds
    const threeSeconds = 3000
    setTimeout(() => setStatusMessage(undefined), threeSeconds)
  }

  const handleCopyManual = async () => {
    logCdfEvent(
      WebInteractClick({ componentName: CDF_COMPONENT_NAME, componentType: `CopyManual[label:${props.label}]` })
    )
  }

  const ValueContainer = props.ValueContainer ?? DefaultValueContainer

  if (isArcadeMigrationEnabled) {
    return (
      <InteractiveCell
        data-testid="copyable-element-interactivecell"
        onClick={handleCopyArcade}
        cellProps={{
          accessory: accessoryIcon,
          body: props.label,
        }}
        label={props.formatted_value ?? props.value}
      />
    )
  }
  return (
    <Container data-testid="copyable-element-container">
      <Stack space={spacing.m}>
        {props.label && <Label>{props.label}</Label>}
        <ValueContainer>
          <span onCopy={handleCopyManual}>{props.formatted_value ?? props.value}</span>
        </ValueContainer>
      </Stack>
      <div>
        {statusMessage === 'success' && (
          <CopySuccess>
            <StyledCheckIcon />
            {t('Copied', { context: 'status text' })}
          </CopySuccess>
        )}
        {statusMessage === 'failed' && (
          <CopyFailed>
            <StyledAlertOutlineIcon size={16} variant="subtle" />
            {t('Failed to copy', { context: 'status text' })}
          </CopyFailed>
        )}
        {!statusMessage && (
          <Button
            variant="tertiary"
            size="icon"
            onClick={handleCopyButton}
            aria-label={props.accessibility_text || t('Copy {label}', { context: 'button text', label: props.label })}
          >
            <CopyIcon color={theme.colors.tertiaryIcon} />
          </Button>
        )}
      </div>
    </Container>
  )
}

export const CopyableElementGroup = (props: FormBlockerElementCopyableElementGroup) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const elements = props.elements ?? []

  if (isArcadeMigrationEnabled) {
    return (
      <Flex flexDirection="column" gap="xl">
        {elements.map(element => {
          return <CopyableElement key={element.label} {...element} />
        })}
      </Flex>
    )
  }
  return (
    <PaddingInset>
      <div>
        {elements.map(element => {
          return <CopyableElement key={element.label} {...element} />
        })}
      </div>
    </PaddingInset>
  )
}
